import { useMutation } from '@tanstack/vue-query'
import { log } from 'src/services/app/log.service'
import { notifyService } from 'src/services/app/notify.service'
import { authService } from 'src/services/api/auth.service'
import { MUTATION_KEYS } from 'src/services/consts/queries.const'
import { resolveDeleteAccountErrorMessage } from 'src/services/helpers/auth.helper'
import { wrapMutationAsync } from 'src/services/helpers/util.helper'
import { useAuthStore } from 'src/stores/auth'

/**
 * Delete-account mutation for admin account settings.
 *
 * Persists via `authService.deleteAccount` (POST /account/delete), then
 * tears down the session via `authStore.logout({ authError: true })`.
 * Cross-tab sync is handled by logout's `BROADCAST_TOPICS.LOGOUT` post.
 *
 * Consumed via `useAccountActionsStore` — components never call this
 * composable directly (project mutation convention).
 */
export function useDeleteAccountMutation() {
  const authStore = useAuthStore()

  const deleteAccountMutation = useMutation({
    mutationKey: [MUTATION_KEYS.DELETE_ACCOUNT],
    mutationFn: () => authService.deleteAccount(),

    onSuccess: async () => {
      await authStore.logout({ authError: true })
    },

    onError: (error) => {
      log.error('[Account] deleteAccount failed', error)
      notifyService.error(resolveDeleteAccountErrorMessage(error))
    },
  })

  return {
    deleteAccount: wrapMutationAsync(deleteAccountMutation.mutateAsync),
    isDeletingAccount: deleteAccountMutation.isPending,
    deleteAccountError: deleteAccountMutation.error,
  }
}
